/**
 * Product-to-cart flying image trail — adapted from the Codrops "From
 * Product to Cart" reference (src/js/products.js,
 * github.com/biazo/codrops-add-to-cart), NOT its cart/state architecture.
 * Only the animation shape is reused: a GSAP timeline animating a
 * staggered set of image layers through a two-stage keyframe path
 * (40% = detach outward, still full size/opacity; 100% = converge on the
 * cart, scaled to 0 and faded out), stagger.from: 'end', duration 1.8s,
 * ease power2.inOut.
 *
 * Differences from the Codrops source, and why:
 * - Codrops' demo ships 5 pre-existing stacked <img> per product card and
 *   just reveals them. A Shopify PDP has one live gallery, not a
 *   pre-built stack, so the layers here are temporary clones created at
 *   click time from the product's own gallery images (real Shopify media,
 *   not placeholder shapes) — the real gallery DOM is never touched.
 * - Codrops computes the landing offset from the cart button's top-left
 *   corner plus fixed per-demo fudge factors tuned to their own ball/icon
 *   sizes. Landing here is computed as a center-to-center delta
 *   (cart center − source center) from live getBoundingClientRect() calls
 *   taken immediately before each flight, so it lands exactly on
 *   .floating-cart-trigger regardless of viewport, scroll position, or
 *   where the cart currently sits — no hardcoded coordinates.
 *
 * Hooks into the theme's existing, already-successful Add to Cart
 * lifecycle — never issues its own cart request and never fires on a
 * failed add. Two entry points feed the single animateProductToCart()
 * below, covering every Add to Cart control in the theme:
 *   1. <product-form> instances (snippets/form.product.liquid) — PDP main
 *      form, Sticky ATC bar — via the `product-form:submit:success` event.
 *   2. Everything else (LRLUX product cards, native Shopify product/
 *      collection/search cards, quick-add, related products, ...) — via
 *      FloatingCart.addToCart() in assets/floating-cart.js, which calls
 *      animateProductToCart() directly on a successful response.
 * Source-image resolution is generic (window.resolveProductCardImage):
 * it walks up from the exact clicked trigger to find that product's own
 * visible image, so it never depends on section-specific selectors.
 */
(function () {
  'use strict';

  var TRAIL_COUNT = 5;
  var STAGGER_EACH = 0.04;
  var FLIGHT_DURATION = 1.8; // seconds — matches the Codrops reference
  var LAYER_Z_INDEX = 2147483550; // above .floating-cart-trigger (2147483500), below the cart drawer (2147483600)

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function getPdpScope() {
    return document.querySelector('.lrlux-pdp');
  }

  function getActiveSlide() {
    var scope = getPdpScope() || document;
    return scope.querySelector('.media-gallery__slide--active');
  }

  /**
   * The image the customer is currently looking at on the PDP gallery —
   * re-resolved on every call (never cached), so switching media before
   * clicking Add to Cart animates the right one. Ignores hidden slides.
   */
  function getActiveProductImage() {
    var activeSlide = getActiveSlide();
    if (!activeSlide) return null;

    var img = activeSlide.querySelector('img');
    if (!img || !(img.currentSrc || img.src)) return null;

    var rect = img.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;

    return img;
  }

  /** Rendered (non-zero size, not display:none/visibility:hidden) — not
   *  necessarily inside the current viewport. */
  function isRenderedImage(img) {
    if (!img || !(img.currentSrc || img.src)) return false;
    var style = window.getComputedStyle(img);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    var rect = img.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  /** Rendered AND currently within the viewport bounds — used to decide
   *  between "main gallery image" vs "sticky bar's own image" for the PDP
   *  sticky ATC, and generally anywhere "is this actually on screen right
   *  now" matters (not just "does it have a size"). */
  function isElementVisible(el) {
    if (!isRenderedImage(el)) return false;
    var rect = el.getBoundingClientRect();
    return (
      rect.bottom > 0 &&
      rect.top < window.innerHeight &&
      rect.right > 0 &&
      rect.left < window.innerWidth
    );
  }

  /**
   * Generic, reusable source-image resolver for ANY Add to Cart trigger
   * anywhere in the theme — LRLUX cards, native Shopify product/collection/
   * search cards, quick-add, related products, carousels, and any future
   * component that follows the same "button lives inside a product card
   * that contains the product's image" pattern. No section-specific class
   * names are hardcoded: it walks up from the exact clicked trigger and
   * returns the first genuinely rendered <img> it finds, which — because
   * it starts at the trigger and climbs *its own* ancestors only — can
   * never accidentally pick up an image from a sibling carousel slide or
   * an unrelated card elsewhere on the page.
   *
   * Exposed as window.resolveProductCardImage so any snippet's own inline
   * script (present or future) can call it at click time, before firing
   * its own cart request.
   */
  function resolveProductCardImage(triggerEl) {
    if (!triggerEl) return null;
    var el = triggerEl;
    var maxDepth = 8;

    for (var depth = 0; depth < maxDepth && el; depth++) {
      if (el.querySelectorAll) {
        var imgs = el.querySelectorAll('img');
        for (var i = 0; i < imgs.length; i++) {
          if (isRenderedImage(imgs[i])) return imgs[i];
        }
      }
      el = el.parentElement;
    }

    return null;
  }

  /**
   * Context-aware source resolution for any <product-form> instance
   * (snippets/form.product.liquid) — covers the PDP main form today, and
   * transparently covers any future component built on the same
   * <product-form> web component.
   *
   * On the PDP specifically: prefer the main gallery's active image when
   * it's actually visible on screen; only fall back to the Sticky ATC
   * bar's own thumbnail when the gallery has scrolled out of view. Never
   * originates from an off-screen image when a visible one exists.
   */
  function resolveContextualSourceImage(productFormEl) {
    var stickyBar = productFormEl && productFormEl.closest && productFormEl.closest('.lrlux-sticky-bar');
    var galleryImg = getActiveProductImage();

    if (stickyBar) {
      if (galleryImg && isElementVisible(galleryImg)) return galleryImg;
      var stickyImg = stickyBar.querySelector('.lrlux-sticky-bar__img');
      if (stickyImg && isElementVisible(stickyImg)) return stickyImg;
      return galleryImg || stickyImg || null;
    }

    if (galleryImg) return galleryImg;

    // Not the PDP gallery form at all (a future <product-form> usage
    // elsewhere, e.g. a card) — fall back to the generic card walk-up.
    return resolveProductCardImage(productFormEl);
  }

  /**
   * Up to TRAIL_COUNT distinct image sources for the trail layers — the
   * active image first, then other real gallery images (the Codrops demo
   * uses several angles of the same product for its layers), padded out
   * with the active image itself if the product doesn't have enough
   * distinct media.
   */
  function getTrailSources(activeImg) {
    var sources = [activeImg.currentSrc || activeImg.src];
    var scope = getPdpScope() || document;
    var otherImgs = scope.querySelectorAll(
      '.media-gallery__slide:not(.media-gallery__slide--active) img'
    );

    for (var i = 0; i < otherImgs.length && sources.length < TRAIL_COUNT; i++) {
      var src = otherImgs[i].currentSrc || otherImgs[i].src;
      if (src && sources.indexOf(src) === -1) sources.push(src);
    }

    var i2 = 0;
    while (sources.length < TRAIL_COUNT) {
      sources.push(sources[i2 % sources.length]);
      i2++;
    }

    return sources.slice(0, TRAIL_COUNT);
  }

  /** Always re-measured immediately before flight — never cached. */
  function getCartTarget() {
    var el = document.getElementById('floating-cart-trigger');
    if (!el) return null;

    // Not offsetParent — .floating-cart-trigger is `position: fixed`, and
    // offsetParent is always null for a fixed-position element regardless
    // of visibility. A real on-screen size is what actually proves it's
    // rendered.
    var style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return null;

    var rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;

    return el;
  }

  /** Reuses the existing pulse (assets/floating-cart.js / floating-cart.css .new-item)
   *  instead of duplicating that animation here. */
  function pulseCartTarget(target) {
    if (window.floatingCart && typeof window.floatingCart.animateFloatingButton === 'function') {
      window.floatingCart.animateFloatingButton();
      return;
    }
    target.classList.add('new-item');
    window.setTimeout(function () {
      target.classList.remove('new-item');
    }, 600);
  }

  /**
   * animateProductToCart(sourceImg)
   *
   * @param {HTMLImageElement|null} sourceImg - image to fly; if omitted,
   *   the currently active PDP gallery image is resolved automatically.
   */
  function animateProductToCart(sourceImg) {
    var cartTarget = getCartTarget();
    if (!cartTarget) return;

    var image = sourceImg || getActiveProductImage();

    if (prefersReducedMotion() || !image || typeof window.gsap === 'undefined') {
      pulseCartTarget(cartTarget);
      return;
    }

    var sourceRect = image.getBoundingClientRect();
    if (sourceRect.width === 0 || sourceRect.height === 0) {
      pulseCartTarget(cartTarget);
      return;
    }

    // Geometry computed once, up front, from live viewport-relative rects
    // — position: fixed on every layer means no scroll-offset math is
    // needed, and nothing here re-measures mid-flight.
    var targetRect = cartTarget.getBoundingClientRect();

    var sourceCenterX = sourceRect.left + sourceRect.width / 2;
    var sourceCenterY = sourceRect.top + sourceRect.height / 2;
    var cartCenterX = targetRect.left + targetRect.width / 2;
    var cartCenterY = targetRect.top + targetRect.height / 2;

    // Relative delta from the source to the cart — GSAP's `x`/`y` are
    // translate offsets from each layer's own current position, so this
    // is all that's needed to land exactly on the cart's center, however
    // far away or wherever on screen it currently sits.
    var targetDeltaX = cartCenterX - sourceCenterX;
    var targetDeltaY = cartCenterY - sourceCenterY;

    // Stage 1 direction (Codrops' top-row/bottom-row concept, adapted):
    // lift away from the product vertically before converging on the
    // cart, toward whichever side of the image has more room in the
    // viewport, so the detach is actually visible instead of clipping.
    var spaceAbove = sourceRect.top;
    var spaceBelow = window.innerHeight - sourceRect.bottom;
    var liftUp = spaceAbove >= spaceBelow;
    var liftDistance = Math.max(sourceRect.height * 0.35, 60);
    var midY = liftUp ? -liftDistance : liftDistance;

    var sources = getTrailSources(image);
    var borderRadius = window.getComputedStyle(image).borderRadius;

    var layers = sources.map(function (src) {
      var el = document.createElement('img');
      el.src = src;
      el.alt = '';
      el.setAttribute('aria-hidden', 'true');
      el.className = 'lrlux-fly-trail__layer';
      // Real current size of the visible product image — never a fixed
      // small box. Shrinking happens progressively via `scale` in the
      // timeline below, not by starting undersized.
      el.style.width = sourceRect.width + 'px';
      el.style.height = sourceRect.height + 'px';
      el.style.left = sourceRect.left + 'px';
      el.style.top = sourceRect.top + 'px';
      el.style.borderRadius = borderRadius;
      el.style.zIndex = LAYER_Z_INDEX;
      document.body.appendChild(el);
      return el;
    });

    var cleanup = function () {
      layers.forEach(function (el) {
        el.remove();
      });
    };

    var tl = window.gsap.timeline({
      onComplete: function () {
        cleanup();
        pulseCartTarget(cartTarget);
      }
    });

    tl.to(layers, {
      keyframes: {
        '40%': {
          x: 0,
          y: midY,
          scale: 0.85,
          opacity: 1
        },
        '100%': {
          x: targetDeltaX,
          y: targetDeltaY,
          scale: 0,
          opacity: 0
        }
      },
      stagger: {
        from: 'end',
        each: STAGGER_EACH
      },
      duration: FLIGHT_DURATION,
      ease: 'power2.inOut'
    });
  }

  window.animateProductToCart = animateProductToCart;
  window.resolveProductCardImage = resolveProductCardImage;

  // <product-form> flows (snippets/form.product.liquid) — PDP main form,
  // Sticky ATC bar, and any future component built on the same web
  // component. The source image is resolved and stashed at *submit* time
  // (capture phase — runs before <product-form>'s own submit handler, i.e.
  // before the async cart request even starts), not after the request
  // resolves: the customer's UI state (scroll position, selected media)
  // could have changed by then. `product-form:submit:success` only ever
  // fires after Shopify's own cart request has actually succeeded, so this
  // never animates a failed add.
  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM' || !form.closest) return;
      var productForm = form.closest('product-form');
      if (!productForm) return;
      productForm.__lrluxFlySource = resolveContextualSourceImage(productForm);
    },
    true
  );

  document.addEventListener('product-form:submit:success', function (e) {
    var productForm = e.target;
    var source = (productForm && productForm.__lrluxFlySource) || getActiveProductImage();
    if (productForm) delete productForm.__lrluxFlySource;
    animateProductToCart(source);
  });

  // Every other Add to Cart flow in the theme (LRLUX product cards, native
  // Shopify product/collection/search cards, quick-add, related products,
  // the legacy ajax-submit form, etc.) ultimately calls
  // window.addToFloatingCart() / FloatingCart.addToCart() — see
  // assets/floating-cart.js, which triggers this same animation directly
  // on a successful response using the sourceImage its caller resolved via
  // window.resolveProductCardImage() at click/submit time. That keeps
  // there being exactly one animation trigger per flow (no double-firing
  // between a "custom" handler and a "global" one) and means this file
  // never needs a per-section listener — see
  // snippets/floating-cart-integration.liquid and
  // snippets/floating-cart-handler.liquid for where sourceImage is
  // resolved for those flows.
})();
